import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { DffGroupedContent, formatDate, trim } from "@dffedb/util";
import { InstallationMaalerAflaesningKvitteringService } from "@e-forsyning/common/maaler";

import { FlyttemeddelelseAdresse, IFlyttemeddelelse } from "../model/flyttemeddelelse.model";

@Injectable({ providedIn: "root" })
export class FlyttemeddelelseKvitteringService {
    constructor(private readonly kvitteringService: InstallationMaalerAflaesningKvitteringService) {}

    public genererKvittering(model: IFlyttemeddelelse): Observable<DffGroupedContent> {
        const maalerAflaesningKvittering$ = this.kvitteringService.genererKvittering(model.aflaesning);
        return maalerAflaesningKvittering$.pipe(
            map((maalerAflaesningKvitering) => this.hentIndtastninger(model, maalerAflaesningKvitering))
        );
    }

    private hentIndtastninger(model: IFlyttemeddelelse, maalerAflaesningKvittering: DffGroupedContent): DffGroupedContent {
        const maalerAflaesningGroups = model.aflaesning.erAflaesningFravalgt ? [] : maalerAflaesningKvittering.groups;
        const kvittering: DffGroupedContent = {
            groups: [
                {
                    title: "Udflytter",
                    rows: [
                        { key: "Ny adresse", value: this.navnOgAdresseToString(model.udflytter.navn, model.udflytter.adresse) },
                        { key: "Mail", value: model.udflytter.mail || "" },
                        { key: "Udflytningsdato", value: this.datoToString(model.udflytter.datoForUdflytning) }
                    ]
                },
                {
                    title: "Indflytter",
                    rows: [
                        { key: "Navn", value: model.indflytter.navn || "" },
                        { key: "Ægtefælle / samlever", value: model.indflytter.navnPaaPartner || "" },
                        { key: "Status", value: this.indflytterStatusToString(model.indflytter.beholdEjerAdresse) }
                    ]
                },
                {
                    title: "Indflytter evt. anden faktureringsadresse",
                    rows: [
                        { key: "Navn", value: model.faktura.navn || "" },
                        { key: "Adresse", value: this.adresseToString(model.faktura.adresse) }
                    ]
                },
                {
                    title: "Anmelder",
                    rows: [
                        { key: "Navn", value: model.anmelder.navn || "" },
                        { key: "Telefonnummer", value: model.anmelder.telefonnr || "" }
                    ]
                },
                {
                    title: "Evt. udbetaling overføres til",
                    rows: [
                        { key: "Reg.nr.", value: model.udbetaling.registreringsnr || "" },
                        { key: "Kontonr.", value: model.udbetaling.kontonr || "" }
                    ]
                },
                ...maalerAflaesningGroups
            ]
        };
        return kvittering;
    }

    private datoToString(dato: Date | string): string {
        const dateFormat = "DD-MM-YYYY";
        return typeof dato === "string" ? formatDate(new Date(dato), dateFormat) : formatDate(dato, dateFormat);
    }

    private indflytterStatusToString(beholdEjerAdresse: boolean | string): string {
        const erLejer = beholdEjerAdresse === true || beholdEjerAdresse === "1";
        const erEjer = beholdEjerAdresse === false || beholdEjerAdresse === "0";
        return erLejer ? "Lejer" : erEjer ? "Ejer" : "";
    }

    private navnOgAdresseToString(navn: string, adresse: FlyttemeddelelseAdresse): string {
        const result = `${navn || ""}<br/>${this.adresseToString(adresse)}`;
        return trim(result);
    }

    private adresseToString(adresse: FlyttemeddelelseAdresse): string {
        const result = `${adresse.vejnavn || ""}<br/>${adresse.postnr || ""} ${adresse.bynavn || ""}`;
        return trim(result, ", ");
    }
}
